import { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { API } from "@/lib/api";
import DashboardLayout from "@/components/DashboardLayout";
import SmartImage from "@/components/SmartImage";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ProfilTabs } from "@/components/TalentSectionTabs";
import PortfolioCustomizer from "@/components/PortfolioCustomizer";
import PortfolioTemplateSelector from "@/components/PortfolioTemplateSelector";
import FocalPointPicker from "@/components/FocalPointPicker";
import {
  Plus, Upload, Star, Trash2, Camera, X as XIcon, Sparkles,
  MapPin, Palette, Eye, ExternalLink, ImagePlus,
} from "lucide-react";
import { toast } from "sonner";

const CATEGORIES = [
  "Shooting", "Beauté", "Éditorial", "Streetwear",
  "Makeup", "Runway", "Collaboration", "Projet personnel",
];

const COLLAB_TYPES = [
  "Shooting photo", "Défilé / Runway", "Campagne publicitaire",
  "Lookbook", "Éditorial mode", "Clip / Vidéo", "Événement",
  "E-commerce", "Beauté", "Streetwear",
];

/* ---------- TagInput : ajout libre mais limité ---------- */
function TagInput({ value = [], onChange, placeholder, suggestions = [], max = 10, testid }) {
  const [input, setInput] = useState("");
  const add = (v) => {
    const t = (v ?? input).trim();
    if (!t) return;
    if (value.length >= max) { toast.error(`Max ${max} éléments`); return; }
    if (value.includes(t)) return;
    onChange([...value, t]);
    setInput("");
  };
  const remove = (t) => onChange(value.filter((x) => x !== t));
  return (
    <div>
      <div className="flex flex-wrap gap-2 mb-3">
        {value.map((t) => (
          <span
            key={t}
            className="inline-flex items-center gap-1.5 pl-3 pr-2 py-1 rounded-full bg-[#FAF3EA] text-[#1A1A1A] text-xs border border-[#EADDC9]"
          >
            {t}
            <button type="button" onClick={() => remove(t)} className="hover:text-[#D32F2F]">
              <XIcon className="w-3 h-3" />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <Input
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder={placeholder}
          className="rounded-xl"
          data-testid={testid}
        />
        <Button type="button" variant="outline" onClick={() => add()} className="rounded-xl">
          <Plus className="w-4 h-4" />
        </Button>
      </div>
      {suggestions.length > 0 && value.length < max && (
        <div className="flex flex-wrap gap-1.5 mt-3">
          {suggestions.filter((s) => !value.includes(s)).slice(0, 8).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => add(s)}
              className="px-2.5 py-1 rounded-full text-[11px] border border-dashed border-[#D4A574] text-[#8B7A3F] hover:bg-[#FAF3EA] transition"
            >
              + {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- Photo edit dialog ---------- */
function PhotoMetaDialog({ photo, open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", category: "", featured: false, focal_x: 0.5, focal_y: 0.5 });
  useEffect(() => {
    if (photo) setForm({
      title: photo.title || "",
      description: photo.description || "",
      category: photo.category || "",
      featured: !!photo.featured,
      focal_x: typeof photo.focal_x === "number" ? photo.focal_x : 0.5,
      focal_y: typeof photo.focal_y === "number" ? photo.focal_y : 0.5,
    });
  }, [photo]);

  const save = async () => {
    try {
      await api.put(`/portfolio/${photo.id}/meta`, form);
      toast.success("Photo mise à jour");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.userMessage || err.response?.data?.detail || "Mise à jour impossible");
    }
  };

  if (!photo) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Détails de la photo</DialogTitle>
          <DialogDescription className="text-sm text-[#666]">
            Définissez le point d'intérêt en cliquant sur l'image, puis ajustez les métadonnées.
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-6 lg:grid-cols-2">
          <FocalPointPicker
            photo={photo}
            onChange={({ focal_x, focal_y }) => setForm((f) => ({ ...f, focal_x, focal_y }))}
          />
          <div className="grid gap-4 content-start">
            <div>
              <Label className="caption-label">Titre</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1" data-testid="photo-meta-title" />
            </div>
            <div>
              <Label className="caption-label">Description courte</Label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" data-testid="photo-meta-desc" />
            </div>
            <div>
              <Label className="caption-label">Catégorie</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger className="rounded-xl mt-1" data-testid="photo-meta-category">
                  <SelectValue placeholder="Sélectionner une catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center justify-between pt-1">
              <div>
                <Label className="caption-label m-0">Mettre à la une</Label>
                <p className="text-xs text-[#8B8680] mt-0.5">Photos mises en avant sur votre portfolio</p>
              </div>
              <Switch checked={form.featured} onCheckedChange={(v) => setForm({ ...form, featured: v })} />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Annuler</Button>
          <Button onClick={save} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="photo-meta-save">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ---------- Project dialog ---------- */
function ProjectDialog({ open, onOpenChange, onSaved }) {
  const [form, setForm] = useState({ title: "", description: "", role: "", date: "" });
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!form.title.trim()) { toast.error("Titre requis"); return; }
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append("title", form.title);
      fd.append("description", form.description);
      fd.append("role", form.role);
      fd.append("date", form.date);
      if (file) fd.append("file", file);
      await api.post("/portfolio/projects", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Projet ajouté");
      setForm({ title: "", description: "", role: "", date: "" });
      setFile(null);
      onSaved?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl">Nouveau projet</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4">
          <div>
            <Label className="caption-label">Titre</Label>
            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="rounded-xl mt-1" placeholder="Ex. Campagne Lacoste SS25" data-testid="project-title" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="caption-label">Rôle</Label>
              <Input value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="rounded-xl mt-1" placeholder="Modèle principal" data-testid="project-role" />
            </div>
            <div>
              <Label className="caption-label">Date</Label>
              <Input value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-xl mt-1" placeholder="Sept. 2025" data-testid="project-date" />
            </div>
          </div>
          <div>
            <Label className="caption-label">Description</Label>
            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="rounded-xl mt-1" data-testid="project-desc" />
          </div>
          <div>
            <Label className="caption-label">Image de couverture</Label>
            <div className="mt-1 flex items-center gap-3">
              <Input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0])} className="rounded-xl" data-testid="project-file" />
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Annuler</Button>
          <Button onClick={submit} disabled={saving} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="project-save">
            {saving ? "Enregistrement…" : "Ajouter le projet"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============ Main Page ============ */
export default function TalentPortfolioPage() {
  const { user, profile, loading, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const initialTab = ["photos", "universe", "projects", "availability", "signature", "customize"].includes(searchParams.get("tab"))
    ? searchParams.get("tab")
    : "photos";
  const [photos, setPhotos] = useState([]);
  const [projects, setProjects] = useState([]);
  const [pprofile, setPProfile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [metaPhoto, setMetaPhoto] = useState(null);
  const [projectOpen, setProjectOpen] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/");
    if (!loading && user && user.role !== "talent") navigate("/");
  }, [user, loading, navigate]);

  const loadAll = async () => {
    if (!user?.user_id) return;
    const [ph, pr, pp] = await Promise.all([
      api.get(`/portfolio/${user.user_id}`),
      api.get(`/portfolio/projects/${user.user_id}`),
      api.get(`/portfolio-profile/${user.user_id}`),
    ]);
    setPhotos(ph.data);
    setProjects(pr.data);
    setPProfile(pp.data);
  };

  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, [user]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/portfolio/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      await loadAll();
      await checkAuth();
      toast.success("Photo ajoutée");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur d'upload");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleSetMain = async (id) => {
    await api.post(`/portfolio/${id}/set-main`);
    await loadAll();
    toast.success("Photo principale mise à jour");
  };

  const handleDelete = async (id) => {
    await api.delete(`/portfolio/${id}`);
    await loadAll();
    await checkAuth();
    toast.success("Photo supprimée");
  };

  const updatePProfile = async (patch) => {
    const { data } = await api.put("/portfolio-profile/me", patch);
    setPProfile(data);
  };

  const savePProfileDebounced = async () => {
    if (!pprofile) return;
    const { data } = await api.put("/portfolio-profile/me", pprofile);
    setPProfile(data);
    toast.success("Univers sauvegardé");
  };

  const mainPhoto = useMemo(() => photos.find((p) => p.is_main), [photos]);
  const gallery = useMemo(() => photos.filter((p) => !p.is_main), [photos]);
  const canUpload = profile?.is_premium || photos.length < 8;

  if (!user || !profile || !pprofile) return null;

  return (
    <DashboardLayout>
      <ProfilTabs />

      {/* Premium banner — editorial preview teaser */}
      <div
        className="relative overflow-hidden rounded-3xl mb-8 cursor-pointer group bg-gradient-to-br from-[#1A1A1A] via-[#2a1d12] to-[#1A1A1A] border border-[#C68E59]/30"
        onClick={() => window.open(`/talents/${user.user_id}`, "_blank")}
        data-testid="editorial-preview-banner"
      >
        <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-[#C68E59]/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-20 -left-10 w-80 h-80 rounded-full bg-[#E6C29A]/10 blur-3xl pointer-events-none" />
        <div className="relative p-6 sm:p-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-5">
          <div className="flex items-start gap-4">
            <div className="hidden sm:flex w-12 h-12 rounded-full bg-[#C68E59]/20 items-center justify-center flex-shrink-0">
              <Eye className="w-5 h-5 text-[#E6C29A]" />
            </div>
            <div>
              <p className="text-[10px] tracking-[0.32em] uppercase text-[#E6C29A] font-medium">Nouveau · Édition limitée</p>
              <h3 className="font-serif text-2xl sm:text-3xl text-white mt-1.5 leading-tight">
                Votre portfolio en version <em className="italic text-[#E6C29A]">éditoriale</em>
              </h3>
              <p className="text-sm text-white/65 mt-2 max-w-md">
                Découvrez votre nouveau profil public — une expérience premium qui donne envie aux marques de vous contacter.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 text-xs tracking-[0.22em] uppercase text-[#E6C29A] sm:flex-shrink-0 sm:translate-x-0 group-hover:translate-x-1 transition-transform">
            Voir mon profil <span className="w-6 h-px bg-[#E6C29A]" /> →
          </div>
        </div>
      </div>

      {/* Page header (sub-section) */}
      <div className="flex flex-wrap items-start justify-between gap-4 mb-8">
        <div>
          <p className="caption-label text-[#C68E59]">Portfolio créatif</p>
          <h2 className="font-serif text-3xl sm:text-4xl text-[#1A1A1A] mt-1">
            Galerie & univers
          </h2>
          <p className="text-[#666] mt-2 text-sm max-w-xl">
            Construisez une page qui raconte votre style, vos inspirations et vos projets. C'est votre vitrine auprès des marques.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => window.open(`/talents/${user.user_id}`, "_blank")}
            className="rounded-full"
            data-testid="preview-portfolio-btn"
          >
            <Eye className="w-4 h-4 mr-2" /> Prévisualiser
          </Button>
        </div>
      </div>

      <Tabs defaultValue={initialTab} className="w-full">
        <TabsList className="mb-8 bg-[#FAF3EA] p-1 rounded-full overflow-x-auto max-w-full">
          <TabsTrigger value="photos" data-testid="tab-photos" className="rounded-full">Galerie</TabsTrigger>
          <TabsTrigger value="style" data-testid="tab-style" className="rounded-full">Style</TabsTrigger>
          <TabsTrigger value="universe" data-testid="tab-universe" className="rounded-full">Mon univers</TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-projects" className="rounded-full">Expériences</TabsTrigger>
          <TabsTrigger value="availability" data-testid="tab-availability" className="rounded-full">Disponibilités</TabsTrigger>
          <TabsTrigger value="signature" data-testid="tab-signature" className="rounded-full">Ma signature</TabsTrigger>
          <TabsTrigger value="customize" data-testid="tab-customize" className="rounded-full">Personnalisation</TabsTrigger>
        </TabsList>

        {/* =========== PHOTOS / GALERIE =========== */}
        <TabsContent value="photos">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-serif text-2xl text-[#1A1A1A]">Ma galerie</h2>
              <p className="text-xs text-[#8B8680] mt-1">
                {photos.length} photo{photos.length > 1 ? "s" : ""}
                {!profile.is_premium && " · Free (3 photos max pour démarrer)"}
              </p>
            </div>
            <div>
              <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={handleFileSelect} data-testid="file-input" />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={!canUpload || uploading}
                className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white"
                data-testid="upload-photo-btn"
              >
                {uploading ? (<><Upload className="w-4 h-4 mr-2 animate-pulse" /> Envoi…</>) : (<><Plus className="w-4 h-4 mr-2" /> Ajouter</>)}
              </Button>
            </div>
          </div>

          {!canUpload && (
            <div className="mb-6 p-4 rounded-2xl bg-[#FAF3EA] border border-[#E6C29A] text-sm text-[#8B7320]">
              Limite Free atteinte. Passez Premium pour ajouter plus de photos.
            </div>
          )}

          {/* Featured main photo */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            <div className="lg:col-span-2">
              {mainPhoto ? (
                <div className="relative rounded-3xl overflow-hidden bg-[#FAF3EA] aspect-[4/5] sm:aspect-[16/11] group">
                  <img
                    src={`${API}/files/${mainPhoto.storage_path}`}
                    alt=""
                    decoding="async"
                    className="w-full h-full object-cover"
                    style={{ objectPosition: `${((mainPhoto.focal_x ?? 0.5) * 100).toFixed(2)}% ${((mainPhoto.focal_y ?? 0.5) * 100).toFixed(2)}%` }}
                    data-testid="main-photo"
                  />
                  <div className="absolute top-4 left-4 px-3 py-1 bg-black/70 text-white rounded-full text-[11px] tracking-widest uppercase">
                    Photo principale
                  </div>
                  {mainPhoto.category && (
                    <div className="absolute bottom-4 left-4 px-3 py-1 bg-white/90 rounded-full text-[11px] tracking-widest uppercase text-[#1A1A1A]">
                      {mainPhoto.category}
                    </div>
                  )}
                  <div className="absolute top-4 right-4 flex gap-2">
                    <button onClick={() => setMetaPhoto(mainPhoto)} className="w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow" title="Modifier">
                      <ExternalLink className="w-4 h-4 text-[#1A1A1A]" />
                    </button>
                    <button onClick={() => handleDelete(mainPhoto.id)} className="w-10 h-10 rounded-full bg-white/90 hover:bg-white flex items-center justify-center shadow">
                      <Trash2 className="w-4 h-4 text-[#D32F2F]" />
                    </button>
                  </div>
                </div>
              ) : (
                <div className="rounded-3xl bg-[#FAF3EA] aspect-[16/11] flex flex-col items-center justify-center cursor-pointer hover:bg-[#F0E0C7] transition" onClick={() => fileInputRef.current?.click()}>
                  <Camera className="w-10 h-10 text-[#A3A3A3] mb-3" />
                  <p className="text-[#666] font-medium">Ajoutez votre photo principale</p>
                </div>
              )}
            </div>

            {/* Side gallery preview */}
            <div className="grid grid-cols-2 gap-3 content-start">
              {gallery.slice(0, 6).map((p) => (
                <div key={p.id} className="relative group rounded-2xl overflow-hidden">
                  <SmartImage photo={p} aspect="aspect-square" />
                  {p.featured && (
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-[#FAF3EA] text-[#8B5A2B] text-[10px] border border-[#E6C29A] flex items-center gap-1">
                      <Sparkles className="w-2.5 h-2.5" /> À la une
                    </span>
                  )}
                  <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1.5">
                    <button onClick={() => handleSetMain(p.id)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center" title="Principale">
                      <Star className="w-3.5 h-3.5 text-[#C68E59]" />
                    </button>
                    <button onClick={() => setMetaPhoto(p)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center" title="Détails">
                      <ExternalLink className="w-3.5 h-3.5 text-[#1A1A1A]" />
                    </button>
                    <button onClick={() => handleDelete(p.id)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                      <Trash2 className="w-3.5 h-3.5 text-[#D32F2F]" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Full gallery grid */}
          {gallery.length > 6 && (
            <div>
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-3">Toute la galerie</h3>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {gallery.map((p) => (
                  <div key={p.id} className="relative group rounded-2xl overflow-hidden">
                    <SmartImage photo={p} aspect="aspect-square" />
                    {p.category && (
                      <span className="absolute bottom-2 left-2 px-2 py-0.5 rounded-full bg-white/90 text-[10px] tracking-widest uppercase text-[#1A1A1A]">
                        {p.category}
                      </span>
                    )}
                    <div className="absolute inset-0 bg-black/55 opacity-0 group-hover:opacity-100 transition flex items-center justify-center gap-1.5">
                      <button onClick={() => setMetaPhoto(p)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                        <ExternalLink className="w-3.5 h-3.5 text-[#1A1A1A]" />
                      </button>
                      <button onClick={() => handleDelete(p.id)} className="w-8 h-8 rounded-full bg-white flex items-center justify-center">
                        <Trash2 className="w-3.5 h-3.5 text-[#D32F2F]" />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </TabsContent>

        {/* =========== STYLE / TEMPLATES =========== */}
        <TabsContent value="style">
          <div className="mb-6">
            <h2 className="font-serif text-2xl text-[#1A1A1A]">Style de mon portfolio</h2>
            <p className="text-xs text-[#8B8680] mt-1">
              Choisissez l'identité visuelle de votre page publique. Le style change instantanément les couleurs, typographies et la mise en page.
            </p>
          </div>
          <PortfolioTemplateSelector
            currentId={pprofile.template_id || "minimal_clean"}
            isPremium={!!profile.is_premium}
            onSelect={async (id) => {
              try {
                const { data } = await api.put("/portfolio-profile/me", { template_id: id });
                setPProfile(data);
                toast.success("Style appliqué");
              } catch (err) {
                toast.error(err.response?.data?.detail || "Impossible d'appliquer ce style");
              }
            }}
          />
        </TabsContent>

        {/* =========== UNIVERS =========== */}
        <TabsContent value="universe">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7">
              <div className="flex items-center gap-2 mb-5">
                <Sparkles className="w-4 h-4 text-[#C68E59]" />
                <h3 className="font-serif text-xl text-[#1A1A1A]">Personnalité</h3>
              </div>
              <Label className="caption-label">Tags qui me définissent</Label>
              <div className="mt-2">
                <TagInput
                  testid="tag-mood"
                  value={pprofile.mood_tags}
                  onChange={(v) => setPProfile({ ...pprofile, mood_tags: v })}
                  placeholder="Elegant, Audacieux, Minimaliste…"
                  suggestions={["Elegant", "Audacieux", "Minimaliste", "Urbain", "Romantique", "Rebelle", "Raffiné", "Sauvage"]}
                />
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7">
              <div className="flex items-center gap-2 mb-5">
                <Palette className="w-4 h-4 text-[#C68E59]" />
                <h3 className="font-serif text-xl text-[#1A1A1A]">Styles favoris</h3>
              </div>
              <TagInput
                testid="tag-styles"
                value={pprofile.favorite_styles}
                onChange={(v) => setPProfile({ ...pprofile, favorite_styles: v })}
                placeholder="Haute couture, Streetwear, Beauté…"
                suggestions={["Haute couture", "Streetwear", "Beauté", "Éditorial", "Runway", "Vintage"]}
              />
            </div>

            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7">
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-5">Inspirations</h3>
              <TagInput
                testid="tag-inspirations"
                value={pprofile.inspirations}
                onChange={(v) => setPProfile({ ...pprofile, inspirations: v })}
                placeholder="Paris 90s, Avant-garde japonaise…"
              />
            </div>

            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7">
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-5">Couleurs préférées</h3>
              <TagInput
                testid="tag-colors"
                value={pprofile.favorite_colors}
                onChange={(v) => setPProfile({ ...pprofile, favorite_colors: v })}
                placeholder="Noir, Bordeaux, Crème…"
                suggestions={["Noir", "Ivoire", "Bordeaux", "Or", "Rouge", "Crème", "Bleu nuit"]}
              />
            </div>

            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7 lg:col-span-2">
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-5">Références artistiques</h3>
              <TagInput
                testid="tag-refs"
                value={pprofile.artistic_references}
                onChange={(v) => setPProfile({ ...pprofile, artistic_references: v })}
                placeholder="Helmut Newton, Issey Miyake, Vogue Italia…"
                max={12}
              />
            </div>
          </div>
          <div className="flex justify-end mt-6">
            <Button onClick={savePProfileDebounced} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="save-universe-btn">
              Enregistrer mon univers
            </Button>
          </div>
        </TabsContent>

        {/* =========== PROJECTS =========== */}
        <TabsContent value="projects">
          <div className="flex items-center justify-between mb-5">
            <div>
              <h2 className="font-serif text-2xl text-[#1A1A1A]">Expériences & projets</h2>
              <p className="text-xs text-[#8B8680] mt-1">Montrez ce que vous avez déjà accompli</p>
            </div>
            <Button onClick={() => setProjectOpen(true)} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="add-project-btn">
              <Plus className="w-4 h-4 mr-2" /> Nouveau projet
            </Button>
          </div>

          {projects.length === 0 ? (
            <div className="rounded-3xl bg-[#FAF3EA] p-12 text-center">
              <ImagePlus className="w-10 h-10 text-[#A3A3A3] mx-auto mb-3" />
              <p className="text-[#666]">Aucun projet pour le moment. Ajoutez votre premier projet pour inspirer les marques.</p>
            </div>
          ) : (
            <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {projects.map((p) => (
                <div key={p.id} className="bg-white rounded-2xl border border-[#EADDC9] overflow-hidden group">
                  <div className="aspect-[4/3] bg-[#FAF3EA]">
                    {p.storage_path ? (
                      <img src={`${API}/files/${p.storage_path}`} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-[#B3A78C] text-xs tracking-widest uppercase">sans image</div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex items-center justify-between gap-2">
                      <h4 className="font-serif text-lg text-[#1A1A1A] leading-tight">{p.title}</h4>
                      <button
                        onClick={async () => {
                          try {
                            await api.delete(`/portfolio/projects/${p.id}`);
                            await loadAll();
                            toast.success("Supprimé");
                          } catch (err) {
                            toast.error(err.userMessage || err.response?.data?.detail || "Suppression impossible");
                          }
                        }}
                        className="text-[#B3A78C] hover:text-[#D32F2F]"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-[#8B8680] mt-1">
                      {p.role && <span>{p.role}</span>}
                      {p.date && <span>· {p.date}</span>}
                    </div>
                    {p.description && <p className="text-sm text-[#555] mt-3 line-clamp-3">{p.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        {/* =========== AVAILABILITY =========== */}
        <TabsContent value="availability">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7">
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-5">État de disponibilité</h3>
              <div className="flex items-center justify-between p-4 rounded-2xl bg-[#FAF3EA]">
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">Disponible pour des projets</p>
                  <p className="text-xs text-[#8B8680] mt-0.5">Affiché sur votre profil public</p>
                </div>
                <Switch checked={pprofile.availability_status} onCheckedChange={(v) => setPProfile({ ...pprofile, availability_status: v })} data-testid="sw-available" />
              </div>
              <div className="flex items-center justify-between p-4 rounded-2xl bg-[#FAF3EA] mt-3">
                <div>
                  <p className="text-sm font-medium text-[#1A1A1A]">Prêt(e) à voyager</p>
                  <p className="text-xs text-[#8B8680] mt-0.5">Pour des projets en dehors de ma ville</p>
                </div>
                <Switch checked={pprofile.willing_to_travel} onCheckedChange={(v) => setPProfile({ ...pprofile, willing_to_travel: v })} data-testid="sw-travel" />
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7">
              <div className="flex items-center gap-2 mb-5">
                <MapPin className="w-4 h-4 text-[#C68E59]" />
                <h3 className="font-serif text-xl text-[#1A1A1A]">Villes disponibles</h3>
              </div>
              <TagInput
                testid="tag-cities"
                value={pprofile.preferred_locations}
                onChange={(v) => setPProfile({ ...pprofile, preferred_locations: v })}
                placeholder="Paris, Milan, New York…"
                suggestions={["Paris", "Milan", "Londres", "New York", "Tokyo", "Bruxelles"]}
              />
            </div>

            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7 lg:col-span-2">
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-5">Collaborations recherchées</h3>
              <TagInput
                testid="tag-collabs"
                value={pprofile.collaboration_types}
                onChange={(v) => setPProfile({ ...pprofile, collaboration_types: v })}
                placeholder="Shooting, Campagne, Lookbook…"
                suggestions={COLLAB_TYPES}
                max={8}
              />
            </div>

            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7 lg:col-span-2">
              <Label className="caption-label">Projets rêvés</Label>
              <Textarea
                rows={3}
                value={pprofile.dream_projects}
                onChange={(e) => setPProfile({ ...pprofile, dream_projects: e.target.value })}
                placeholder="Ex. Une campagne pour une maison de haute couture parisienne…"
                className="rounded-xl mt-2"
                data-testid="dream-projects"
              />
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <Button onClick={savePProfileDebounced} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="save-availability-btn">
              Enregistrer
            </Button>
          </div>
        </TabsContent>

        {/* =========== SIGNATURE =========== */}
        <TabsContent value="signature">
          <div className="grid gap-6 lg:grid-cols-2">
            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7 lg:col-span-2">
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-5">Carte d'identité créative</h3>
              <div className="grid gap-4">
                <div>
                  <Label className="caption-label">Métier / Pseudo affiché (headline)</Label>
                  <Input
                    value={pprofile.headline}
                    onChange={(e) => setPProfile({ ...pprofile, headline: e.target.value })}
                    placeholder="Ex. Mannequin éditorial · Paris"
                    className="rounded-xl mt-1"
                    data-testid="inp-headline"
                  />
                </div>
                <div>
                  <Label className="caption-label">Phrase signature</Label>
                  <Input
                    value={pprofile.signature_quote}
                    onChange={(e) => setPProfile({ ...pprofile, signature_quote: e.target.value })}
                    placeholder="« Je crois au geste simple et à la lumière naturelle. »"
                    className="rounded-xl mt-1"
                    data-testid="inp-signature"
                  />
                </div>
                <div>
                  <Label className="caption-label">Bio courte (affichée dans le hero)</Label>
                  <Textarea
                    rows={3}
                    value={pprofile.bio}
                    onChange={(e) => setPProfile({ ...pprofile, bio: e.target.value })}
                    placeholder="Deux ou trois phrases pour planter votre univers."
                    className="rounded-xl mt-1"
                    data-testid="inp-short-bio"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7">
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-5">Ce qui me représente</h3>
              <div className="grid gap-4">
                <div>
                  <Label className="caption-label">Phrase personnelle</Label>
                  <Textarea
                    rows={2}
                    value={pprofile.personal_statement}
                    onChange={(e) => setPProfile({ ...pprofile, personal_statement: e.target.value })}
                    className="rounded-xl mt-1"
                    data-testid="inp-personal"
                  />
                </div>
                <div>
                  <Label className="caption-label">Vision créative</Label>
                  <Textarea
                    rows={2}
                    value={pprofile.creative_vision}
                    onChange={(e) => setPProfile({ ...pprofile, creative_vision: e.target.value })}
                    className="rounded-xl mt-1"
                    data-testid="inp-vision"
                  />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-2xl border border-[#EADDC9] p-7">
              <h3 className="font-serif text-xl text-[#1A1A1A] mb-5">Objectif & inspiration</h3>
              <div className="grid gap-4">
                <div>
                  <Label className="caption-label">Objectif</Label>
                  <Input
                    value={pprofile.main_goal}
                    onChange={(e) => setPProfile({ ...pprofile, main_goal: e.target.value })}
                    className="rounded-xl mt-1"
                    data-testid="inp-goal"
                  />
                </div>
                <div>
                  <Label className="caption-label">Inspiration principale</Label>
                  <Input
                    value={pprofile.main_inspiration}
                    onChange={(e) => setPProfile({ ...pprofile, main_inspiration: e.target.value })}
                    className="rounded-xl mt-1"
                    data-testid="inp-inspiration"
                  />
                </div>
              </div>
            </div>
          </div>

          <div className="flex justify-end mt-6">
            <Button onClick={savePProfileDebounced} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="save-signature-btn">
              Enregistrer
            </Button>
          </div>
        </TabsContent>

        {/* =========== CUSTOMIZE / PERSONNALISATION =========== */}
        <TabsContent value="customize">
          <PortfolioCustomizer
            pprofile={pprofile}
            userId={user.user_id}
            onSaved={(updated) => setPProfile(updated)}
          />
        </TabsContent>
      </Tabs>

      <PhotoMetaDialog
        photo={metaPhoto}
        open={!!metaPhoto}
        onOpenChange={(o) => !o && setMetaPhoto(null)}
        onSaved={loadAll}
      />
      <ProjectDialog open={projectOpen} onOpenChange={setProjectOpen} onSaved={loadAll} />
    </DashboardLayout>
  );
}
